import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Info, Loader2 } from "lucide-react";
import {
    Command,
    CommandGroup,
    CommandInput,
    CommandItem,
} from "@/components/ui/command"
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from 'sonner';
import axiosInstance from '@/utils/axiosInstance';

interface Props {
    isEnabled: boolean;
    config?: any;
}

const emailTypes = [
    { key: 'order_completed', label: 'Order Completed' },
    { key: 'new_order', label: 'New Order' },
    { key: 'order_rejected', label: 'Order Rejected' },
    { key: 'order_approved', label: 'Order Approved' },
];

const SendGridDialog: React.FC<Props> = ({ isEnabled, config }) => {

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [apiKey, setApiKey] = useState('');
    const [senderEmail, setSenderEmail] = useState('');
    const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
    const [templateInputs, setTemplateInputs] = useState<Record<string, string>>({});
    const [templates, setTemplates] = useState<Record<string, string>>({});

    useEffect(() => {
        if (config) {
            setApiKey(config.value || '');
            const json = config.json_value || {};
            setSenderEmail(json.sender_email || '');
            const temp = { ...json };
            delete temp.sender_email;
            setTemplates(temp);
        }
    }, [config]);

    const handleTemplateChange = (key: string, value: string) => {
        setTemplateInputs((prev) => ({
            ...prev,
            [key]: value
        }));
    };

    const handleAddTemplates = async () => {

        setLoading(true);

        const newTemplates: Record<string, string> = {};
        selectedTypes.forEach((type) => {
            const templateId = templateInputs[type];
            if (templateId) {
                newTemplates[type] = templateId;
            }
        });
        setTemplates((prev) => ({
            ...prev,
            ...newTemplates
        }));

        const data = {
            key: 'sendgrid',
            value: apiKey,
            json_value: {
                sender_email: senderEmail,
                ...templateInputs
            }
        };

        if (config.length > 0) {

            try {
                await axiosInstance.put(`/integrations/${config[0].id}`, data);
                toast('SendGrid updated successfully');
                setOpen(false);
            } catch (error) {
                toast('An error occured. Please try again');
                setOpen(true);
            } finally {
                setLoading(false);
            }

        } else {

            try {
                await axiosInstance.post(`/integrations`, data);
                toast('Sendgrid was successfully configured')
                setOpen(false);
            } catch (error) {
                toast('An error occured. Please try again')
                setOpen(true);
            } finally {
                setLoading(false);
            }

        }

    };

    const toggleType = (value: string) => {
        setSelectedTypes((prev) =>
            prev.includes(value)
                ? prev.filter((v) => v !== value)
                : [...prev, value]
        );
    };

    useEffect(() => {
        if (config) {
            setApiKey(config[0].value || '');

            const json = config[0].json_value || {};
            const sender = json.sender_email || '';
            setSenderEmail(sender);

            const temp = { ...json };
            delete temp.sender_email;
            setTemplates(temp);

            const existingTypes = Object.keys(temp);
            setSelectedTypes(existingTypes);

            setTemplateInputs(temp);
        }
    }, [config]);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger>
                {isEnabled ? (
                    <Button variant="outline" className="flex items-center gap-2">
                        <Info size={16} /> Details
                    </Button>
                ) : (
                    <Button variant="outline">Integrate</Button>
                )}
            </DialogTrigger>
            <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                    <DialogTitle>SendGrid Integration</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                    <div>
                        <Label className="mb-3">API Key</Label>
                        <Input
                            placeholder="SG.xxxxx..."
                            value={apiKey}
                            onChange={(e) => setApiKey(e.target.value)}
                        />
                    </div>
                    <div>
                        <Label className="mb-3">Sender Email</Label>
                        <Input
                            placeholder="you@example.com"
                            value={senderEmail}
                            onChange={(e) => setSenderEmail(e.target.value)}
                        />
                    </div>
                    <div>
                        <Label className="mb-3">Email Types</Label>
                        <Command className="rounded-lg border shadow-md md:min-w-[450px]">
                            <CommandInput placeholder="Search email types..." />
                            <CommandGroup>
                                {emailTypes.map((item) => (
                                    <CommandItem
                                        key={item.key}
                                        onSelect={() => toggleType(item.key)}
                                        className="flex items-center justify-between"
                                    >
                                        {item.label}
                                        <Check
                                            className={cn(
                                                "h-4 w-4",
                                                selectedTypes.includes(item.key)
                                                    ? "opacity-100"
                                                    : "opacity-0"
                                            )}
                                        />
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </Command>
                    </div>
                    {selectedTypes.map((type) => (
                        <div key={type}>
                            <Label className="mb-3">{emailTypes.find((e) => e.key === type)?.label} Template ID</Label>
                            <Input
                                className="mt-1"
                                value={templateInputs[type] || ''}
                                onChange={(e) => handleTemplateChange(type, e.target.value)}
                                placeholder="Template ID"
                            />
                        </div>
                    ))}
                    {selectedTypes.length > 0 && (
                        <Button onClick={handleAddTemplates} className="mt-2 flex items-center gap-2" disabled={loading}>
                            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                            {loading ? "Saving..." : "Save Changes"}
                        </Button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default SendGridDialog;